/**
 * Firebase Storage Service
 * Handles file uploads for baby photos
 */

import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';

const storage = getStorage();

/**
 * Upload baby profile photo
 */
export async function uploadBabyPhoto(
  babyId: string,
  file: Blob
): Promise<string> {
  try {
    const storageRef = ref(storage, `babies/${babyId}/profile.jpg`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading baby photo:', error);
    throw new Error('Failed to upload photo');
  }
}

/**
 * Upload prescription photo for a doctor visit
 * Returns the public download URL
 */
export async function uploadPrescriptionPhoto(
  babyId: string,
  visitId: string,
  file: File,
  index = 0
): Promise<string> {
  try {
    const ext = file.name.split('.').pop() || 'jpg';
    const suffix = index === 0 ? '' : `_${index}`;
    const storageRef = ref(storage, `babies/${babyId}/prescriptions/${visitId}${suffix}.${ext}`);
    const snapshot = await uploadBytes(storageRef, file);
    return await getDownloadURL(snapshot.ref);
  } catch (error) {
    console.error('Error uploading prescription photo:', error);
    throw new Error('Failed to upload prescription photo');
  }
}

/**
 * Delete baby profile photo
 */
export async function deleteBabyPhoto(babyId: string): Promise<void> {
  try {
    const storageRef = ref(storage, `babies/${babyId}/profile.jpg`);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting baby photo:', error);
    // Don't throw error if file doesn't exist
  }
}

/**
 * Compress and resize image before upload
 */
export async function compressImage(
  file: File,
  maxWidth: number = 800,
  maxHeight: number = 800,
  quality: number = 0.8
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Create cropped image from crop area
 */
export async function getCroppedImg(
  imageSrc: string,
  croppedAreaPixels: { x: number; y: number; width: number; height: number }
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  canvas.width = croppedAreaPixels.width;
  canvas.height = croppedAreaPixels.height;

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    croppedAreaPixels.width,
    croppedAreaPixels.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      'image/jpeg',
      0.9
    );
  });
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });
}
